import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import './index.scss';

type DrawerComponentProps = {
  show: boolean;
  onHide?: () => void;
  onExit?: () => void;
  onClose?: () => void;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children?: React.ReactNode;
  className?: string;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  disableEscapeListener?: boolean;
  backdrop?: boolean;
  backdropClassName?: string;
  enforceFocus?: boolean;
};

const DrawerV2: React.FC<DrawerComponentProps> = ({
  show,
  onHide,
  onExit,
  onClose,
  size = 'sm',
  placement = 'right',
  children,
  className = '',
  disableEscapeListener = false,
  backdrop = true,
  backdropClassName = '',
  enforceFocus = false,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Call onExit immediately when closing starts
    if (onExit) {
      onExit();
    }
    // Delay the actual close to allow animation to complete
    setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      if (onHide) {
        onHide();
      }
      // Call onClose after the drawer is fully closed
      if (onClose) {
        onClose();
      }
    }, 320); // Slightly longer than animation duration to prevent flickering
  }, [onHide, onExit, onClose]);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (!show && shouldRender && !isClosing) {
      // Only trigger close if drawer is rendered and not already closing
      handleClose();
    }
  }, [show]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !disableEscapeListener && show) {
        handleClose();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [show, disableEscapeListener, handleClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && backdrop) {
      handleClose();
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case 'xs':
      case 'sm':
      case 'md':
      case 'lg':
      case 'xl':
      case 'full':
        return `drawer-v2-${size}`;
      default:
        return 'drawer-v2-sm';
    }
  };

  const getPlacementClass = () => {
    switch (placement) {
      case 'left':
      case 'right':
      case 'top':
      case 'bottom':
        return `drawer-v2-${placement}`;
      default:
        return 'drawer-v2-right';
    }
  };

  if (!shouldRender) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`drawer-v2-overlay ${backdropClassName} ${
        isClosing ? 'drawer-v2-overlay-closing' : ''
      }`}
      onClick={handleBackdropClick}
    >
      <div
        ref={drawerRef}
        className={`drawer-v2-container ${getSizeClass()} ${getPlacementClass()} ${className} ${
          isClosing ? 'drawer-v2-closing' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={enforceFocus ? -1 : undefined}
      >
        <div className="drawer-v2-content">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default DrawerV2;
